// Where the "You on camera" lines land on a SCENES board, so the preview can
// say it. A mirror of the worker (runpod/corrido-v2/planner.py sung_slots,
// scene_artist_shots, moment_kind and _route_artist_moments): one line goes to
// ONE shot of the kind it describes, a line about how he looks goes on the
// sung shots. On today's board one line is all 4 of his shots, and the
// preview keeps saying so. tests/test_planner_scenes.py checks the worker side
// of the same cases as artist-scenes.test.ts.

export type Delivery = "sing" | "scene" | "crew";
/** Where one line lands: `shots` of the `of` shots of kind `to`. `kind` is
 *  what the line describes: a company line on a scene shot makes it a shot
 *  with company (planner.apply_moment), so the page says "with company". */
export type MomentSlot = { to: Delivery; kind: Delivery; shots: number; of: number };

// planner._COMPANY: vision.js's company phrases.
const COMPANY = /\bwith (a young woman|young women|a norte[a-zñ]+ band|bodyguards|his crew|his friends|friends)\b/i;
// planner._ACTION: vision.js's action phrases.
const ACTION = new RegExp(
  "\\b(driving|riding|leaning on|stepping out of|sitting on (?:the|a|an)\\b|standing next to|walking toward"
  + "|playing an?\\b|holding an? (?:beer|glass|thick stack|rifle|pistol)|smoking|dancing|toasting|counting)",
  "i",
);

/** Which of his shots a line steers (planner.moment_kind). */
export function momentKind(moment: string): Delivery {
  if (COMPANY.test(moment || "")) return "crew";
  return ACTION.test(moment || "") ? "scene" : "sing";
}

/** Which of n artist shots (in board order) are sung (planner.sung_slots):
 *  four, today's count (the owner's cost rule), never more than half. */
export function sungSlots(n: number): number[] {
  if (n <= 0) return [];
  const s = Math.min(4, Math.ceil(n / 2));
  return [...new Set(Array.from({ length: s }, (_, i) => Math.floor((2 * i * n + s) / (2 * s))))].sort((a, b) => a - b);
}

/** How many of `rest` non-sung shots are with people (planner.crew_count):
 *  3 in 8, the measured alone / with-people split, rounded half up. */
export function crewCount(rest: number): number {
  return rest > 0 ? Math.floor((3 * rest + 4) / 8) : 0;
}

/** How many shots of each kind a scenes board of `count` artist shots has:
 *  3 in 8 of the shots that are not sung are crew shots, when `crew`. */
export function deliveryCounts(count: number, crew: boolean): Record<Delivery, number> {
  const sing = sungSlots(count).length;
  const rest = count - sing;
  const withPeople = crew ? crewCount(rest) : 0;
  return { sing, scene: rest - withPeople, crew: withPeople };
}

const ROUTE: Record<Delivery, Delivery[]> = { crew: ["crew", "scene", "sing"], scene: ["scene", "sing"], sing: ["sing"] };

/** Where each usable line (in order) lands on a scenes board. */
export function routeArtistMoments(moments: string[], count: number, crew: boolean): MomentSlot[] {
  const size = deliveryCounts(count, crew);
  const used: Record<Delivery, number> = { sing: 0, scene: 0, crew: 0 };
  const to = moments.map((m) => {
    const g = ROUTE[momentKind(m)].find((d) => d === "sing" || used[d] < size[d]) as Delivery;
    used[g] += 1;
    return g;
  });
  // The sung shots split among the lines that land on them, in order: the
  // k-th sung shot takes line min(len-1, floor(k*len/n)).
  const sungLines = used.sing;
  const share = Array.from({ length: sungLines }, () => 0);
  for (let k = 0; k < size.sing && sungLines; k++) share[Math.min(sungLines - 1, Math.floor((k * sungLines) / size.sing))] += 1;
  let nth = 0;
  return to.map((g, i) => ({ to: g, kind: momentKind(moments[i]), shots: g === "sing" ? share[nth++] : 1, of: size[g] }));
}
